import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { creditsApi, subscriptionsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Sparkles, Check, ChevronLeft, CreditCard, Loader2, Star, Zap, Crown, Rocket
} from 'lucide-react';
import { toast } from 'sonner';
import { EmbeddedCheckoutModal } from '../components/EmbeddedCheckout';

const Credits = ({ onBack }) => {
    const { user, refreshUser } = useAuth();
    const [packages, setPackages] = useState({});
    const [tiers, setTiers] = useState({});
    const [loading, setLoading] = useState(null);
    const [pollingSession, setPollingSession] = useState(null);
    
    // Embedded checkout state
    const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
    const [selectedPackageId, setSelectedPackageId] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [checkoutType, setCheckoutType] = useState('credits');
    const [billingPeriod, setBillingPeriod] = useState('monthly');

    useEffect(() => {
        loadData();
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        if (sessionId) {
            pollPaymentStatus(sessionId);
        }
    }, []);

    const loadData = async () => {
        try {
            const [packagesRes, tiersRes] = await Promise.all([
                creditsApi.packages(),
                subscriptionsApi.tiers(),
            ]);
            setPackages(packagesRes.data);
            setTiers(tiersRes.data);
        } catch (error) {
            toast.error('Failed to load pricing');
        }
    };

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 10;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            toast.error('Payment verification timed out. Please contact support if credits are not added.');
            setPollingSession(null);
            return;
        }

        setPollingSession(sessionId);

        try {
            const response = await creditsApi.status(sessionId);
            if (response.data.payment_status === 'paid') {
                toast.success(`Payment successful! ${response.data.credits_added} credits added.`);
                refreshUser();
                setPollingSession(null);
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Payment status check failed:', error);
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        }
    };

    const handlePurchase = async (packageId) => {
        setCheckoutType('credits');
        setSelectedPackageId(packageId);
        setSelectedPlanId(null);
        setShowEmbeddedCheckout(true);
    };
    
    const handleSubscribe = async (planId) => {
        setCheckoutType('subscription');
        setSelectedPlanId(planId);
        setSelectedPackageId(null);
        setShowEmbeddedCheckout(true);
    };
    
    const handleCheckoutSuccess = () => {
        toast.success('Payment successful!');
        refreshUser();
        setShowEmbeddedCheckout(false);
        setSelectedPackageId(null);
        setSelectedPlanId(null);
    };

    // Calculate savings and cost per credit
    const getPackageDetails = (id, pkg) => {
        const basePrice = 0.10; // Base price per credit (starter package)
        const costPerCredit = pkg.price / pkg.credits;
        const savings = Math.round((1 - (costPerCredit / basePrice)) * 100);
        
        const icons = {
            'starter': <Zap className="h-6 w-6" />,
            'popular': <Star className="h-6 w-6" />,
            'value': <Crown className="h-6 w-6" />,
            'pro': <Rocket className="h-6 w-6" />
        };
        
        const labels = {
            'starter': null,
            'popular': { text: 'Most Popular', color: 'bg-blue-500' },
            'value': { text: 'Best Value', color: 'bg-green-500' },
            'pro': { text: 'Pro Choice', color: 'bg-amber-500' }
        };
        
        return {
            costPerCredit: costPerCredit.toFixed(2),
            savings,
            icon: icons[id] || <Sparkles className="h-6 w-6" />,
            label: labels[id]
        };
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <h1 className="font-serif text-2xl font-semibold">Credits & Billing</h1>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {pollingSession && (
                    <div className="mb-6 p-4 bg-accent/10 rounded-lg flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-accent" />
                        <span>Verifying your payment...</span>
                    </div>
                )}

                {/* Current Balance */}
                <Card className="mb-8">
                    <CardHeader>
                        <CardTitle className="font-serif flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-accent" />
                            Your Balance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-4xl font-serif font-bold">{user?.credits || 0}</div>
                                <div className="text-muted-foreground">credits available</div>
                            </div>
                            <div className="text-right">
                                <Badge variant="outline" className="mb-2">
                                    {user?.subscription_tier?.toUpperCase() || 'FREE'} Plan
                                </Badge>
                                <p className="text-sm text-muted-foreground">
                                    {user?.subscription_credits_remaining || 0} monthly credits remaining
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Credit Bundles - Main Purchase Section */}
                <div className="mb-8">
                    <div className="text-center mb-6">
                        <h2 className="font-serif text-2xl font-semibold mb-2">Buy Credit Bundles</h2>
                        <p className="text-muted-foreground">Save more when you buy larger bundles • Instant delivery</p>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries(packages).map(([id, pkg]) => {
                            const details = getPackageDetails(id, pkg);
                            const isPopular = id === 'popular';
                            const isBest = id === 'value';
                            
                            return (
                                <Card 
                                    key={id} 
                                    className={`relative transition-all hover:shadow-lg ${
                                        isBest ? 'ring-2 ring-green-500 shadow-lg' : 
                                        isPopular ? 'ring-2 ring-blue-500' : ''
                                    }`}
                                >
                                    {/* Label Badge */}
                                    {details.label && (
                                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white ${details.label.color}`}>
                                            {details.label.text}
                                        </div>
                                    )}
                                    
                                    <CardContent className="pt-8 pb-6">
                                        {/* Icon */}
                                        <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                                            isBest ? 'bg-green-100 text-green-600' :
                                            isPopular ? 'bg-blue-100 text-blue-600' :
                                            'bg-accent/10 text-accent'
                                        }`}>
                                            {details.icon}
                                        </div>
                                        
                                        {/* Credits Amount */}
                                        <div className="text-center mb-4">
                                            <div className="font-serif text-4xl font-bold">{pkg.credits.toLocaleString()}</div>
                                            <div className="text-muted-foreground text-sm">credits</div>
                                        </div>
                                        
                                        {/* Price */}
                                        <div className="text-center mb-2">
                                            <span className="font-serif text-3xl font-bold">${pkg.price}</span>
                                        </div>
                                        
                                        {/* Cost per credit */}
                                        <div className="text-center mb-4">
                                            <span className="text-sm text-muted-foreground">
                                                ${details.costPerCredit} per credit
                                            </span>
                                            {details.savings > 0 && (
                                                <Badge className="ml-2 bg-green-500 text-white text-xs">
                                                    Save {details.savings}%
                                                </Badge>
                                            )}
                                        </div>
                                        
                                        {/* Buy Button */}
                                        <Button 
                                            className={`w-full ${
                                                isBest ? 'bg-green-600 hover:bg-green-700' :
                                                isPopular ? 'bg-blue-600 hover:bg-blue-700' :
                                                ''
                                            }`}
                                            size="lg"
                                            onClick={() => handlePurchase(id)}
                                            disabled={loading === id}
                                            data-testid={`buy-${id}`}
                                        >
                                            {loading === id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <CreditCard className="h-4 w-4 mr-2" />
                                                    Buy Now
                                                </>
                                            )}
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                    
                    {/* Trust indicators */}
                    <div className="flex justify-center gap-6 mt-6 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <Check className="h-4 w-4 text-green-500" />
                            <span>Secure payment</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Check className="h-4 w-4 text-green-500" />
                            <span>Instant delivery</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Check className="h-4 w-4 text-green-500" />
                            <span>Never expires</span>
                        </div>
                    </div>
                </div>

                {/* Subscription Tiers */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-serif text-xl font-semibold">Monthly Subscription Plans</h2>
                        <div className="flex items-center gap-2 bg-muted rounded-full p-1">
                            <button 
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    billingPeriod === 'monthly' 
                                        ? 'bg-background shadow-sm' 
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('monthly')}
                            >
                                Monthly
                            </button>
                            <button 
                                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                                    billingPeriod === 'annual' 
                                        ? 'bg-background shadow-sm' 
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                                onClick={() => setBillingPeriod('annual')}
                            >
                                Annual
                                <Badge className="bg-green-500 text-white text-xs ml-1">Save 15%</Badge>
                            </button>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries(tiers).map(([id, tier]) => {
                            const annualPrice = tier.price * 12 * 0.85;
                            const displayPrice = billingPeriod === 'annual' 
                                ? (annualPrice / 12).toFixed(0) 
                                : tier.price;
                            const isCurrentPlan = user?.subscription_tier === id;
                            
                            return (
                                <Card 
                                    key={id} 
                                    className={isCurrentPlan ? 'ring-2 ring-accent' : ''}
                                >
                                    <CardContent className="pt-6">
                                        <div className="text-center mb-4">
                                            <h3 className="font-serif text-lg font-semibold">{tier.name}</h3>
                                            <div className="flex items-baseline justify-center gap-1 mt-2">
                                                <span className="font-serif text-3xl font-bold">
                                                    ${displayPrice}
                                                </span>
                                                <span className="text-muted-foreground">/mo</span>
                                            </div>
                                            {billingPeriod === 'annual' && tier.price > 0 && (
                                                <p className="text-xs text-green-600 mt-1">
                                                    ${annualPrice.toFixed(0)}/year (Save ${(tier.price * 12 - annualPrice).toFixed(0)})
                                                </p>
                                            )}
                                        </div>
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2 text-sm">
                                                <Check className="h-4 w-4 text-green-500" />
                                                <span>{tier.credits_monthly} credits/month</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm">
                                                <Check className="h-4 w-4 text-green-500" />
                                                <span>${tier.credit_price}/credit overage</span>
                                            </div>
                                        </div>
                                        <Button 
                                            variant={isCurrentPlan ? "outline" : "default"}
                                            className="w-full"
                                            disabled={isCurrentPlan || id === 'free'}
                                            onClick={() => handleSubscribe(id)}
                                            data-testid={`subscribe-${id}`}
                                        >
                                            {isCurrentPlan ? 'Current Plan' : id === 'free' ? 'Free' : 'Subscribe'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>

                {/* What You Can Do With Credits */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-serif">What Can You Do With Credits?</CardTitle>
                        <CardDescription>Use credits for AI-powered features</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">AI Writing Assist</span>
                                    <Badge variant="outline">1 credit</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">Get AI suggestions to improve your writing</p>
                            </div>
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Hook Creation</span>
                                    <Badge variant="outline">2 credits</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">Generate compelling opening hooks</p>
                            </div>
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Story Arc Analysis</span>
                                    <Badge variant="outline">3 credits</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">Analyze and improve your story structure</p>
                            </div>
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Story Help</span>
                                    <Badge variant="outline">2 credits</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">Get guidance on plot, characters, and more</p>
                            </div>
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Standard Illustration</span>
                                    <Badge variant="outline">5 credits</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">AI-generated book illustrations (fal.ai)</p>
                            </div>
                            <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium">Premium Illustration</span>
                                    <Badge variant="outline">10 credits</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">High-quality illustrations (OpenAI)</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </main>
            
            {/* Embedded Checkout Modal */}
            <EmbeddedCheckoutModal
                isOpen={showEmbeddedCheckout}
                onClose={() => {
                    setShowEmbeddedCheckout(false);
                    setSelectedPackageId(null);
                    setSelectedPlanId(null);
                }}
                checkoutType={checkoutType}
                planId={selectedPlanId}
                packageId={selectedPackageId}
                billingPeriod={billingPeriod}
                onSuccess={handleCheckoutSuccess}
            />
        </div>
    );
};

export default Credits;
